import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSave } from 'react-icons/md';
import ToastContainer, { useToast } from '../../components/ToastContainer';
import { t } from '../../utils/i18n';
import api from '../../services/api';

export default function CreateJobPage() {
  const navigate = useNavigate();
  const { showToast, toasts, removeToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    scheduledDate: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '17:00',
    hourlyRate: '',
    location: {
      address: '',
      city: ''
    }
  });

  const handleInputChange = (key, value) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      setForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setForm(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.description || !form.location.address) {
      showToast(t('jobs.fillAllFields') || 'Molimo popunite sva polja', 'error', 4000);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...form,
        hourlyRate: parseFloat(form.hourlyRate) || 0
      };
      
      const response = await api.post('/jobs', payload);
      if (response.data) {
        showToast(t('jobs.created') || 'Posao je uspešno kreiran', 'success', 4000);
        setTimeout(() => {
          navigate('/dashboard/jobs', { replace: true });
        }, 500);
      }
    } catch (error) {
      console.error('Error creating job:', error);
      const message = error.response?.data?.message || t('jobs.createError') || 'Greška pri kreiranju posla';
      showToast(message, 'error', 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden relative">
      <div className="relative z-10 p-4 lg:p-6">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/dashboard/jobs')}
            className="mb-6 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
          >
            <MdArrowBack className="text-xl" />
            <span>{t('common.back') || 'Back'}</span>
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 lg:p-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              {t('jobs.create') || 'Kreiraj posao'}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('jobs.title') || 'Naslov'} *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                  placeholder={t('jobs.titlePlaceholder') || 'Npr. Tražim dadilju za vikend'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('jobs.description') || 'Opis'} *
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                  placeholder={t('jobs.descriptionPlaceholder') || 'Detaljan opis posla...'}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('jobs.date') || 'Datum'} *
                  </label>
                  <input
                    type="date"
                    value={form.scheduledDate}
                    onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('jobs.startTime') || 'Početak'} *
                  </label>
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={(e) => handleInputChange('startTime', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('jobs.endTime') || 'Kraj'} *
                  </label>
                  <input
                    type="time"
                    value={form.endTime}
                    onChange={(e) => handleInputChange('endTime', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('jobs.hourlyRate') || 'Cena radnog sata (RSD/h)'} *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.hourlyRate}
                  onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                  placeholder="800"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('jobs.address') || 'Adresa'} *
                  </label>
                  <input
                    type="text"
                    value={form.location.address}
                    onChange={(e) => handleInputChange('location.address', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                    placeholder={t('jobs.addressPlaceholder') || 'Ulica i broj'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('jobs.city') || 'Grad'} *
                  </label>
                  <input
                    type="text"
                    value={form.location.city}
                    onChange={(e) => handleInputChange('location.city', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                    placeholder={t('jobs.cityPlaceholder') || 'Beograd'}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <MdSave />
                  {isSubmitting ? (t('common.saving') || 'Čuvanje...') : (t('common.save') || 'Sačuvaj')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/jobs')}
                  className="btn-secondary"
                >
                  {t('common.cancel') || 'Otkaži'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

